/**
 * Report Editor - Edit report content with change tracking and versioning
 */

import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { Modal } from '../UI/Modal';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { getReport, downloadReportV2 } from '../../api/reports';
import { ReportSectionEditorCard, type ReportSectionEditorCardRef } from './ReportSectionEditorCard';
import {
  Save,
  X,
  RotateCcw,
  GitBranch,
  AlertCircle,
  CheckCircle,
  Edit3,
  FileText,
  Target,
  TrendingDown,
} from 'lucide-react';

interface ReportEditorProps {
  reportId: string;
  onSave: () => void;
  onCancel: () => void;
}

interface Section {
  title: string;
  content: string;
  hasChanges: boolean;
  wordCount?: number;
}

interface OptimizationSuggestion {
  section: string;
  original_text: string;
  optimized_text: string;
  word_reduction: number;
  confidence: number;
}

export function ReportEditor({ reportId, onSave, onCancel }: ReportEditorProps) {
  const [sections, setSections] = useState<Section[]>([]);
  const [originalContent, setOriginalContent] = useState<string>('');
  const [saveMessage, setSaveMessage] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [showWordOptimizer, setShowWordOptimizer] = useState(false);
  const [targetWordCount, setTargetWordCount] = useState<number | null>(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationSuggestions, setOptimizationSuggestions] = useState<OptimizationSuggestion[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<OptimizationSuggestion | null>(null);
  const queryClient = useQueryClient();
  const sectionRefs = useRef<(ReportSectionEditorCardRef | null)[]>([]);

  const { data: report, isLoading } = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => getReport(reportId),
    enabled: !!reportId,
  });

  // Load and parse report content
  useEffect(() => {
    if (reportId) {
      downloadReportV2(reportId)
        .then(content => {
          setOriginalContent(content);
          // Parse markdown content into sections
          const parsedSections = parseMarkdownSections(content);
          setSections(parsedSections.map(s => ({ ...s, hasChanges: false })));
        })
        .catch(err => console.error('Failed to load report:', err));
    }
  }, [reportId]);

  const parseMarkdownSections = (markdown: string): Section[] => {
    const sections: Section[] = [];
    const lines = markdown.split('\n');
    let currentSection: Section | null = null;
    let currentContent: string[] = [];

    for (const line of lines) {
      // Check for section headers (##)
      if (line.startsWith('## ')) {
        // Save previous section
        if (currentSection) {
          currentSection.content = currentContent.join('\n').trim();
          currentSection.wordCount = currentSection.content.split(/\s+/).filter(w => w.length > 0).length;
          sections.push(currentSection);
        }
        // Start new section
        currentSection = {
          title: line.replace('## ', '').trim(),
          content: '',
          hasChanges: false,
        };
        currentContent = [];
      } else if (currentSection) {
        currentContent.push(line);
      }
    }

    // Add last section
    if (currentSection) {
      currentSection.content = currentContent.join('\n').trim();
      currentSection.wordCount = currentSection.content.split(/\s+/).filter(w => w.length > 0).length;
      sections.push(currentSection);
    }

    return sections;
  };

  const handleSectionEdit = (index: number, newContent: string) => {
    const newSections = [...sections];
    newSections[index].content = newContent;
    newSections[index].hasChanges = true;
    newSections[index].wordCount = newContent.split(/\s+/).filter(w => w.length > 0).length;
    setSections(newSections);
    setIsDirty(true);
  };

  const handleOptimizeWordCount = async () => {
    if (!targetWordCount || !report) return;
    
    setIsOptimizing(true);
    try {
      // Calculate current word count
      const currentWordCount = sections.reduce((sum, s) => sum + (s.wordCount || 0), 0);
      
      if (currentWordCount <= targetWordCount) {
        alert('Current word count is already at or below target');
        setIsOptimizing(false);
        return;
      }

      // In production, this would call the optimization API
      // For now, generate suggestions based on section word counts
      const suggestions: OptimizationSuggestion[] = sections
        .filter(s => (s.wordCount || 0) > 100)
        .map(s => ({
          section: s.title,
          original_text: s.content.substring(0, 300) + '...',
          optimized_text: s.content.substring(0, Math.floor(s.content.length * 0.85)) + '...',
          word_reduction: Math.floor((s.wordCount || 0) * 0.15),
          confidence: 0.75,
        }));

      setOptimizationSuggestions(suggestions);
    } catch (error: any) {
      console.error('Optimization failed:', error);
      alert(`Optimization failed: ${error.message}`);
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleApplyOptimization = (suggestion: OptimizationSuggestion) => {
    const sectionIndex = sections.findIndex(s => s.title === suggestion.section);
    if (sectionIndex >= 0) {
      handleSectionEdit(sectionIndex, suggestion.optimized_text);
      setSelectedSuggestion(null);
    }
  };

  const totalWordCount = sections.reduce((sum, s) => sum + (s.wordCount || 0), 0);

  const handleSave = async () => {
    // Reconstruct markdown from section refs (latest content) or fallback to sections state
    const contents = sections.map((s, i) => sectionRefs.current[i]?.getContent() ?? s.content);
    const updatedMarkdown = sections
      .map((s, i) => `## ${s.title}\n\n${contents[i]}`)
      .join('\n\n');

    setSaveMessage('Creating new version...');
    
    try {
      // Call API to save edited report as new version
      const { saveReportContent } = await import('../../api/reports');
      const result = await saveReportContent(reportId, updatedMarkdown, 'v2');
      
      setSaveMessage('');
      setIsDirty(false);
      setSections(sections.map(s => ({ ...s, hasChanges: false })));
      
      // Invalidate queries to refresh report data
      const { useQueryClient } = await import('@tanstack/react-query');
      const queryClient = useQueryClient();
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      
      onSave();
    } catch (error: any) {
      console.error('Failed to save report:', error);
      setSaveMessage(`Failed to save: ${error.message || 'Unknown error'}`);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleRevert = () => {
    if (window.confirm('Discard all changes and revert to saved version?')) {
      const parsedSections = parseMarkdownSections(originalContent);
      setSections(parsedSections.map(s => ({ ...s, hasChanges: false })));
      setIsDirty(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Editor Header */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Edit3 className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{report?.name || report?.client_name || 'Untitled Report'}</h2>
              <p className="text-sm text-gray-600">
                Editing report - Changes will create a new version
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            {/* Word Count Display */}
            {report && (
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                <FileText className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">
                  {totalWordCount.toLocaleString()} words
                  {(report as any).metadata?.word_limit && (
                    <span className={`ml-2 ${totalWordCount > (report as any).metadata.word_limit ? 'text-red-600' : 'text-gray-500'}`}>
                      / {(report as any).metadata.word_limit.toLocaleString()} limit
                    </span>
                  )}
                </span>
              </div>
            )}
            {(report as any)?.metadata?.word_limit && totalWordCount > (report as any).metadata.word_limit && (
              <Button
                variant="outline"
                onClick={() => setShowWordOptimizer(true)}
                className="flex items-center gap-2"
              >
                <Target className="h-4 w-4" />
                Optimize Word Count
              </Button>
            )}
            {isDirty && (
              <Button
                variant="outline"
                onClick={handleRevert}
                className="flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Revert Changes
              </Button>
            )}
            <Button
              variant="outline"
              onClick={onCancel}
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isDirty || !!saveMessage}
              className="flex items-center gap-2"
            >
              {saveMessage ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  {saveMessage}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save as New Version
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Change Indicator */}
        {isDirty && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm text-yellow-800">
              You have unsaved changes - Save to create version {(report?.updated_at ? 2 : 1)}
            </span>
          </div>
        )}
      </Card>

      {/* Section Editors - isolated state so typing does not re-render entire ReportEditor */}
      <div className="space-y-6">
        {sections.map((section, idx) => (
          <Card key={idx}>
            <ReportSectionEditorCard
              ref={(r) => { sectionRefs.current[idx] = r; }}
              sectionTitle={section.title}
              initialContent={section.content}
              sectionIndex={idx}
              onRevertSection={handleRevertSection}
              onContentChange={handleSectionContentChange}
              debounceMs={400}
            />
          </Card>
        ))}
      </div>

      {/* Save Footer */}
      {isDirty && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg p-4 z-10">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {sections.filter(s => s.hasChanges).length} section(s) modified
              </span>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleRevert}>
                Discard Changes
              </Button>
              <Button onClick={handleSave} disabled={!!saveMessage}>
                <GitBranch className="h-4 w-4" />
                Save as Version {(report?.updated_at ? 2 : 1)}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Word Optimization Modal */}
      {showWordOptimizer && (
        <Modal
          isOpen={showWordOptimizer}
          onClose={() => {
            setShowWordOptimizer(false);
            setOptimizationSuggestions([]);
            setTargetWordCount(null);
          }}
          title="Optimize Word Count"
          size="lg"
        >
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Current Word Count</span>
                <span className="text-2xl font-bold">{totalWordCount.toLocaleString()}</span>
              </div>
              {(report as any)?.metadata?.word_limit && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Target Limit</span>
                  <span className="text-lg font-semibold">{(report as any).metadata.word_limit.toLocaleString()}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Target Word Count</label>
              <Input
                type="number"
                placeholder={(report as any)?.metadata?.word_limit?.toString() || 'e.g., 4000'}
                value={targetWordCount || ''}
                onChange={(e) => setTargetWordCount(e.target.value ? parseInt(e.target.value) : null)}
              />
              {targetWordCount && totalWordCount > targetWordCount && (
                <div className="mt-2 p-3 bg-yellow-50 rounded-lg">
                  <div className="flex items-center gap-2 text-sm">
                    <TrendingDown className="h-4 w-4 text-yellow-600" />
                    <span className="text-yellow-900">
                      Need to reduce by {(totalWordCount - targetWordCount).toLocaleString()} words (
                      {(((totalWordCount - targetWordCount) / totalWordCount) * 100).toFixed(1)}%)
                    </span>
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={handleOptimizeWordCount}
              disabled={isOptimizing || !targetWordCount || targetWordCount >= totalWordCount}
              className="w-full"
            >
              {isOptimizing ? (
                <>
                  <LoadingSpinner className="mr-2" />
                  Optimizing...
                </>
              ) : (
                <>
                  <Target className="h-4 w-4 mr-2" />
                  Generate Optimization Suggestions
                </>
              )}
            </Button>

            {optimizationSuggestions.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                <p className="text-sm font-medium">Optimization Suggestions</p>
                {optimizationSuggestions.map((suggestion, idx) => (
                  <div
                    key={idx}
                    className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedSuggestion(suggestion)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{suggestion.section}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Save {suggestion.word_reduction} words ({Math.round(suggestion.confidence * 100)}% confidence)
                        </p>
                      </div>
                      <Button variant="ghost" size="sm">
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Optimization Suggestion Detail Modal */}
      {selectedSuggestion && (
        <Modal
          isOpen={!!selectedSuggestion}
          onClose={() => setSelectedSuggestion(null)}
          title={`Optimization: ${selectedSuggestion.section}`}
          size="lg"
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-2">Original Text</p>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm">{selectedSuggestion.original_text}</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Optimized Text</p>
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm">{selectedSuggestion.optimized_text}</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Word Reduction</p>
                <p className="text-lg font-bold text-blue-900">
                  {selectedSuggestion.word_reduction} words
                </p>
              </div>
              <div>
                <p className="text-sm font-medium">Confidence</p>
                <p className="text-lg font-bold text-blue-900">
                  {Math.round(selectedSuggestion.confidence * 100)}%
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setSelectedSuggestion(null)}>
                Cancel
              </Button>
              <Button onClick={() => handleApplyOptimization(selectedSuggestion)}>
                Apply Optimization
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

